import React, { useState } from "react";
import { MentorAvatar } from "./Logo";
import { Sparkles, ArrowRight, ShieldCheck, Check, ChevronRight, Award, Trophy, Timer } from "lucide-react";

export default function ResumePainPage({ onOpenModal }) {
  const [atsScore, setAtsScore] = useState(null);
  const [scannerRole, setScannerRole] = useState("");
  const [scanning, setScanning] = useState(false);
  const [sliderVal, setSliderVal] = useState(50);
  
  // Guarantee Calculator States
  const [exp, setExp] = useState(2);
  const [targetSalary, setTargetSalary] = useState(15);

  const triggerAtsScan = (e) => {
     e.preventDefault();
    if (!scannerRole) return;
    setScanning(true);
    setAtsScore(null);
    setTimeout(() => {
      setScanning(false);
      setAtsScore(Math.floor(Math.random() * 20) + 38); // Standard low ATS score
    }, 2000);
  };

  return (
    <div className="bg-white text-[#0B192C] font-sans pt-20">
      
      {/* 1. Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#F4F7FA] to-white py-16 md:py-24 border-b border-[#D1D5DB]">
        {/* Subtle grid pattern background */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#e5e7eb_1px,transparent_1px),linear-gradient(to_bottom,#e5e7eb_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-60" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Left Hero Column */}
          <div className="lg:col-span-7 space-y-6">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest bg-blue-500/10 text-blue-600 border border-blue-500/20">
              <Sparkles className="h-3 w-3" /> The Tier-2/3 Placement Bridge
            </span>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-[#0B192C] leading-[1.1]" data-testid="hero-heading">
              No campus placements, <span className="text-blue-600">invisible resume?</span>
            </h1>
            
            <h2 className="text-lg sm:text-xl font-extrabold text-slate-700 tracking-tight leading-relaxed">
              Unlock Your Career Clarity: From stuck in a Tier 2/3 city to hired in top tech companies.
            </h2>
            
            <p className="text-slate-500 text-sm sm:text-base max-w-xl leading-relaxed">
              Elite college graduates have premium mentorship and placement assistance. You don't. We bridge this massive gap, teaching you how to build a resume that outcompetes metro-city graduates.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                onClick={() => onOpenModal("https://learn.vedulaconsulting.com/web/checkout/6a5ec65348643a0e7836ee0e", "Book Free Clarity Call", "Resume Pain Strategy")}
                className="px-6 py-4 bg-blue-600 hover:bg-blue-700 text-white font-black text-sm uppercase rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-blue-500/25 transition-all"
                data-testid="book-free-clarity-call-btn"
              >
                Book Free Clarity Call <ArrowRight className="h-4 w-4" />
              </button>
              <button
                onClick={() => onOpenModal("https://learn.vedulaconsulting.com/web/checkout/69bedaedd6964e0f829ec5c2", "Register for Free Webinar", "Resume Pain Strategy")}
                className="px-6 py-4 bg-slate-100 hover:bg-slate-200 text-slate-800 font-black text-sm uppercase rounded-xl flex items-center justify-center gap-2 transition-all border border-slate-300"
                data-testid="register-for-free-webinar-btn"
              >
                Register for Free Webinar
              </button>
            </div>
          </div>

          {/* Right Hero Column: Interactive ATS Simulator Widget */}
          <div className="lg:col-span-5 bg-[#F4F7FA] border border-slate-200/80 rounded-3xl p-6 shadow-xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-24 h-24 bg-blue-500/10 rounded-full blur-3xl" />
            
            <div className="flex items-center gap-2 mb-4 border-b border-slate-200 pb-3">
              <span className="w-3.5 h-3.5 rounded-full bg-blue-600 animate-pulse" />
              <span className="text-xs font-black uppercase tracking-wider text-slate-700">Tier-2/3 Resume Score Simulator</span>
            </div>

            <form onSubmit={triggerAtsScan} className="space-y-4">
              <div>
                <label className="block text-[11px] font-black uppercase text-slate-500 tracking-wider mb-1">Target Tech Role</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Associate Software Engineer"
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-blue-600"
                  value={scannerRole}
                  onChange={(e) => setScannerRole(e.target.value)}
                  data-testid="ats-scanner-role-input"
                />
              </div>

              <div>
                <label className="block text-[11px] font-black uppercase text-slate-500 tracking-wider mb-1">Paste Your Resume Summary or College Projects</label>
                <textarea
                  rows="3"
                  required
                  placeholder="Paste your college final year projects or standard experience..."
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-blue-600 resize-none"
                  data-testid="ats-scanner-resume-input"
                />
              </div>

              <button
                type="submit"
                disabled={scanning}
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-xs font-black uppercase tracking-wider rounded-xl flex items-center justify-center gap-2 transition-all"
                data-testid="ats-scanner-submit-btn"
              >
                {scanning ? "Evaluating Competitive Strength..." : "Scan My Resume Now"}
              </button>
            </form>

            {/* Scanning Laser Line */}
            {scanning && (
              <div className="absolute left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-indigo-500 blur-sm animate-bounce mt-4" />
            )}

            {/* Result Scan */}
            {atsScore !== null && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-2xl animate-fade-in">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-extrabold text-slate-700">Competitive Strength vs Tier-1 Grads:</span>
                  <span className="text-lg font-black text-red-600" data-testid="ats-score-result">{atsScore}%</span>
                </div>
                <div className="w-full bg-slate-200 h-2 rounded-full overflow-hidden mt-1.5">
                  <div className="bg-red-500 h-full rounded-full" style={{ width: `${atsScore}%` }} />
                </div>
                <div className="text-xs text-red-700 font-bold mt-2 leading-relaxed">
                  ⚠️ COMPETITIVE ALERT: Standard college projects like "E-Commerce Web" get auto-rejected. You are competing with elite grads who build production-ready projects.
                </div>
              </div>
            )}
          </div>

        </div>
      </section>

      {/* 2. Pain Identification */}
      <section className="py-16 md:py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <span className="text-xs font-black uppercase tracking-wider text-blue-600">The Hard Truth</span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-[#0B192C] mt-2 mb-4" data-testid="pain-heading">
            Is this your struggle?
          </h2>
          <div className="space-y-4 text-lg text-slate-600 font-medium">
            <p className="flex items-center gap-2" data-testid="pain-bullet">
              <span className="w-2.5 h-2.5 rounded-full bg-red-600" /> Discarded because of college tier or village location.
            </p>
            <p className="flex items-center gap-2" data-testid="pain-bullet">
              <span className="w-2.5 h-2.5 rounded-full bg-red-600" /> No premium campus placements or reference networks.
            </p>
            <p className="flex items-center gap-2" data-testid="pain-bullet">
              <span className="w-2.5 h-2.5 rounded-full bg-red-600" /> Resume gets filtered out instantly by automated hiring software.
            </p>
          </div>
          <div className="mt-6 border-l-4 border-red-600 pl-4 py-2 italic font-black text-slate-800 text-lg md:text-xl leading-relaxed">
            "It’s not just rejection. It’s the frustration of not knowing how to compete with elite graduates."
          </div>
        </div>
      </section>

      {/* 3. Behind the Pain (Storytelling Case Study) */}
      <section className="bg-slate-50 py-16 border-t border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-4 flex justify-center">
            <MentorAvatar />
          </div>
          <div className="md:col-span-8 space-y-4">
            <span className="text-xs font-black uppercase tracking-wider text-blue-600">Small Town Triumph</span>
            <h3 className="text-2xl font-extrabold text-slate-900 tracking-tight">
              Rahul from a small village applied to 300+ jobs. No responses.
            </h3>
            <p className="text-slate-600 leading-relaxed text-sm sm:text-base">
              "My college had no placement cell, and my resume looked just like every other graduate's. I thought tech careers were only for metro city kids. Mr. Vedula audited my resume, replaced my generic projects with premium production architectures, and helped me secure 3 callbacks in top-tier organizations within a month."
            </p>
            <div className="flex items-center gap-4 pt-2">
              <div className="text-xs font-bold uppercase tracking-wider bg-emerald-500/10 text-emerald-700 px-3 py-1 rounded-full border border-emerald-500/20">
                Placement Secured
              </div>
              <div className="text-xs font-bold uppercase tracking-wider bg-blue-500/10 text-blue-700 px-3 py-1 rounded-full border border-blue-500/20">
                Bridged from Village to Corporate
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Resume Bullet Slider */}
      <section className="py-16 bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 text-center space-y-6">
          <span className="text-xs font-black uppercase tracking-wider text-blue-600">Actionable Showcase</span>
          <h3 className="text-2xl sm:text-3xl font-extrabold tracking-tight">The Power of Action-Packed Bullets</h3>
          <p className="text-slate-500 text-sm max-w-xl mx-auto">
            Drag the slider below to see how standard "weak" bullets are rescued by Mr. Vedula’s elite copywriting frameworks.
          </p>

          <div className="bg-slate-50 p-6 rounded-3xl border border-slate-200/80 text-left mt-4">
            <div className="space-y-4">
              <div>
                <span className="text-xs font-black uppercase tracking-widest text-red-500 block mb-1">Standard / Weak Bullet (ATS Match 30%):</span>
                <p className="text-sm text-slate-600 bg-red-500/5 p-3 rounded-xl border border-red-100 font-medium">
                  "Responsible for building frontend features and optimizing system databases to improve performance."
                </p>
              </div>
              
              <div>
                <span className="text-xs font-black uppercase tracking-widest text-emerald-600 block mb-1">Vedula-Rescued Elite Bullet (ATS Match 98%):</span>
                <p className="text-sm text-slate-800 bg-emerald-500/5 p-3 rounded-xl border border-emerald-100 font-bold">
                  "Architected distributed microservices and engineered highly performant GraphQL modules; reduced database latency by 42% and unlocked 30k+ DAU scale."
                </p>
              </div>
            </div>
            
            <div className="mt-6">
              <label className="block text-[10px] font-black uppercase text-slate-400 mb-2 text-center">Drag to simulate impact scale</label>
              <input
                type="range"
                min="0"
                max="100"
                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                value={sliderVal}
                onChange={(e) => setSliderVal(e.target.value)}
                data-testid="resume-slider-handle"
              />
              <div className="flex justify-between text-xs text-slate-500 font-bold mt-2">
                <span>Stuck / Ignored</span>
                <span className="text-blue-600 font-black">Rescued Score: {sliderVal}%</span>
                <span>FAANG Interview Ready</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Solution Framework */}
      <section className="py-16 md:py-24 bg-[#0B192C] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <span className="text-xs font-black uppercase tracking-wider text-blue-400">The Solution</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white" data-testid="solution-heading">
              Your Career GPS System
            </h2>
            <p className="text-slate-400 text-sm sm:text-base leading-relaxed">
              Mr. Vedula’s proven four-pillar framework designed to steer you directly past standard gatekeepers to executive roles.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl space-y-3 hover:-translate-y-1 transition-all">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 text-blue-400 flex items-center justify-center font-black">01</div>
              <h3 className="font-extrabold text-lg text-white">Resume Rescue</h3>
              <p className="text-slate-400 text-xs sm:text-sm leading-relaxed">
                Reposition your profile, bypass robotic ATS filters, and craft high-impact bullet copy that forces action.
              </p>
            </div>
            
            <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl space-y-3 hover:-translate-y-1 transition-all">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 text-blue-400 flex items-center justify-center font-black">02</div>
              <h3 className="font-extrabold text-lg text-white">Interview Anchor</h3>
              <p className="text-slate-400 text-xs sm:text-sm leading-relaxed">
                Establish unshakable system design, coding clarity, and bulletproof communication frameworks.
              </p>
            </div>

            <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl space-y-3 hover:-translate-y-1 transition-all">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 text-blue-400 flex items-center justify-center font-black">03</div>
              <h3 className="font-extrabold text-lg text-white">Gap Fillers</h3>
              <p className="text-slate-400 text-xs sm:text-sm leading-relaxed">
                Bridge experience gaps using real production-grade microservices and live-architecture capstones.
              </p>
            </div>

            <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl space-y-3 hover:-translate-y-1 transition-all">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 text-blue-400 flex items-center justify-center font-black">04</div>
              <h3 className="font-extrabold text-lg text-white">Dream 50 Tracker</h3>
              <p className="text-slate-400 text-xs sm:text-sm leading-relaxed">
                Systematically trace, target, and capture senior roles in your top 50 highly sought companies.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 6. Silver Membership Pitch */}
      <section className="py-16 md:py-24 bg-slate-50 border-t border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 bg-[#0B192C] text-white rounded-3xl p-8 md:p-12 relative overflow-hidden shadow-2xl">
          <div className="absolute top-0 right-0 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl" />
          
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center relative">
            <div className="md:col-span-8 space-y-6">
              <span className="inline-block px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider bg-amber-500 text-[#0B192C]">
                👑 Platinum Standard Curriculum
              </span>
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight" data-testid="silver-heading">
                Upgrade to Silver Membership
              </h2>
              <div className="space-y-3">
                <p className="flex items-center gap-2 text-sm text-slate-300">
                  <Check className="h-4 w-4 text-amber-500 stroke-[3]" /> <strong>Resume Rescue Blueprint</strong> (Full copy blueprint templates)
                </p>
                <p className="flex items-center gap-2 text-sm text-slate-300">
                  <Check className="h-4 w-4 text-amber-500 stroke-[3]" /> <strong>Gap Fillers Workbook</strong> (Step-by-step microservices architecture)
                </p>
                <p className="flex items-center gap-2 text-sm text-slate-300">
                  <Check className="h-4 w-4 text-amber-500 stroke-[3]" /> <strong>Interview Anchor Toolkit</strong> (STAR behavioral worksheets)
                </p>
                <p className="flex items-center gap-2 text-sm text-slate-300">
                  <Check className="h-4 w-4 text-amber-500 stroke-[3]" /> <strong>90-Day Production Playbook</strong> (Step-by-step career path roadmap)
                </p>
              </div>
              <div className="pt-2">
                <span className="text-xs text-amber-400 font-bold block mb-1">🎁 EXCLUSIVE INCLUDED BONUSES:</span>
                <span className="text-xs text-slate-400 font-medium">3 Free 1-on-1 Clarity Calls with Mr. Vedula + Weekly Lifetime Q&A webinars</span>
              </div>
            </div>
            
            <div className="md:col-span-4 bg-slate-900 border border-slate-800 p-6 rounded-2xl text-center space-y-4">
              <div className="text-slate-400 text-xs font-black uppercase">One Lifetime Fee</div>
              <div className="text-4xl font-black text-amber-500">₹4,999</div>
              <button
                onClick={() => onOpenModal("https://learn.vedulaconsulting.com/web/checkout/6a187ad45eaf79ab7261b65e", "Join Silver Membership Now", "Resume Strategy Silver Membership")}
                className="w-full py-4 bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-sm uppercase rounded-xl shadow-lg shadow-amber-500/15 transition-all"
                data-testid="join-silver-membership-btn"
              >
                Join Silver Now
              </button>
              <span className="text-[10px] text-slate-500 block">100% Satisfaction Backed Assurance</span>
            </div>
          </div>
        </div>
      </section>

      {/* 5. CTA Options Section */}
      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        <div className="text-center space-y-2">
          <span className="text-xs font-black uppercase tracking-wider text-blue-600">Secure Your Slot</span>
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Flexible Career Acceleration Pathways</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="border border-slate-200/80 bg-slate-50/50 p-6 rounded-3xl flex flex-col justify-between hover:shadow-xl transition-all">
            <div className="space-y-3">
              <span className="text-xs font-black text-blue-600 uppercase tracking-widest block">1-on-1 Guidance</span>
              <h3 className="font-extrabold text-xl text-[#0B192C]">Free Clarity Call</h3>
              <p className="text-slate-500 text-sm leading-relaxed">
                Book a personalized, FREE 30-minute diagnostic clarity call with Mr. Vedula. Get a complete, direct assessment.
              </p>
            </div>
            <button
              onClick={() => onOpenModal("https://learn.vedulaconsulting.com/web/checkout/6a5ec65348643a0e7836ee0e", "Book Free Clarity Call", "Resume Strategy Free Diagnostic")}
              className="w-full mt-6 py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all"
              data-testid="cta-clarity-call-btn"
            >
              Book Call (3 slots left)
            </button>
          </div>

          <div className="border border-slate-200/80 bg-slate-50/50 p-6 rounded-3xl flex flex-col justify-between hover:shadow-xl transition-all">
            <div className="space-y-3">
              <span className="text-xs font-black text-blue-600 uppercase tracking-widest block">Live Interactive Masterclass</span>
              <h3 className="font-extrabold text-xl text-[#0B192C]">Exclusive Live Webinar</h3>
              <p className="text-slate-500 text-sm leading-relaxed">
                Join our next live masterclass showing the exact blueprint to bypass recruitment robots and lock in high salary packages.
              </p>
            </div>
            <button
              onClick={() => onOpenModal("https://learn.vedulaconsulting.com/web/checkout/69bedaedd6964e0f829ec5c2", "Register for Free Webinar", "Resume Strategy Live Webinar")}
              className="w-full mt-6 py-3.5 bg-slate-800 hover:bg-slate-900 text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all"
              data-testid="cta-webinar-btn"
            >
              Register for Next Webinar
            </button>
          </div>

          <div className="border border-amber-300 bg-amber-500/5 p-6 rounded-3xl flex flex-col justify-between hover:shadow-xl transition-all">
            <div className="space-y-3">
              <span className="text-xs font-black text-amber-600 uppercase tracking-widest block">Instant Career Launch</span>
              <h3 className="font-extrabold text-xl text-slate-900">Silver Membership Access</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Instant access to all framework blueprint workbooks, weekly mastermind modules, and career checklists.
              </p>
            </div>
            <button
              onClick={() => onOpenModal("https://vedulaprogress.lovable.app", "Join Silver Membership Now", "Resume Strategy Silver Membership")}
              className="w-full mt-6 py-3.5 bg-amber-500 hover:bg-amber-600 text-slate-950 font-black text-xs uppercase tracking-wider rounded-xl transition-all"
              data-testid="cta-silver-btn"
            >
              Join Silver Membership Now
            </button>
          </div>
        </div>
      </section>

    </div>
  );
}
